import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
} from '@mui/material';
import EmptyMessage from 'core/components/EmptyMessage';
import { useHolidays } from 'hooks/useHolidays';
import { isNil } from 'lodash';
import { useId } from 'react';
import { checkToday, checkWeekend, compareDays } from '../../utils';
import { getEventsCount } from '../utils';
import DayTitle from './DayTitle';
import type { DayEvent } from '../types';
import type { FC } from 'react';

interface MonthDaysListProps {
  groupedByMonthAndDay: Array<{
    month: Date;
    days: DayEvent[];
  }>;
  selectedDay: DayEvent | null;
  handleDayClick: (day: DayEvent) => void;
}

const dateFormatter = new Intl.DateTimeFormat('ru', {
  day: '2-digit',
  month: '2-digit',
});

export const MonthDaysList: FC<MonthDaysListProps> = ({
  groupedByMonthAndDay,
  selectedDay,
  handleDayClick,
}) => {
  const componentId = useId();
  const dateRange = {
    start: groupedByMonthAndDay[0]?.days[0]?.date ?? new Date(),
    end:
      groupedByMonthAndDay[groupedByMonthAndDay.length - 1]?.days?.[
        (groupedByMonthAndDay[groupedByMonthAndDay.length - 1]?.days?.length ?? 1) - 1
      ]?.date ?? new Date(),
  };

  const { holidays } = useHolidays(dateRange);

  if (groupedByMonthAndDay.length === 0) {
    return (
      <Box sx={{ px: 1 }}>
        <EmptyMessage message='События не найдены' />
      </Box>
    );
  }

  return (
    <List sx={{ p: 0 }}>
      {groupedByMonthAndDay.map((monthGroup) => (
        <Box key={componentId + monthGroup.month.toISOString()}>
          <ListItem sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <ListItemText
              primary={monthGroup.month.toLocaleString('ru', {
                month: 'long',
                year: 'numeric',
              })}
            />
          </ListItem>
          <Divider />
          {monthGroup.days.map((day) => {
            const isWeekend = checkWeekend(day.date);
            const isToday = checkToday(day.date);
            const holidayInfo = holidays.find((h) => h.date.diff(day.date, 'day') === 0);
            if (holidayInfo?.isHoliday === true)
              console.log(
                holidayInfo,
                day.date,
                holidayInfo.date,
                holidayInfo.date.diff(day.date, 'day')
              );

            return (
              <ListItemButton
                key={day.date.toISOString()}
                selected={
                  !isNil(selectedDay) ? compareDays(selectedDay.date, day.date) : false
                }
                onClick={() => {
                  handleDayClick(day);
                }}
                sx={{
                  ...(isToday && {
                    bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                    },
                  }),
                }}>
                <ListItemText
                  primary={
                    <DayTitle
                      date={day.date}
                      isHoliday={holidayInfo?.isHoliday ?? false}
                      isShortDay={holidayInfo?.isShortDay ?? false}
                      isWeekend={isWeekend}
                      isToday={isToday}
                      holidayName={holidayInfo?.name}
                      dateFormatter={dateFormatter}
                      showTooltip={true}
                    />
                  }
                  slotProps={{
                    primary: {
                      fontWeight: isToday ? 'medium' : undefined,
                      color:
                        holidayInfo?.isHoliday === true && !isWeekend
                          ? 'warning.main'
                          : isWeekend
                          ? 'error.main'
                          : isToday
                          ? 'primary.main'
                          : undefined,
                    },
                  }}
                  secondary={<>{getEventsCount(day)}</>}
                />
              </ListItemButton>
            );
          })}
          <Divider />
        </Box>
      ))}
    </List>
  );
};
